import { useState, useEffect } from 'react';
import { ToastProps, ToastState, ToastReturn } from './types';

const TOAST_LIMIT = 1;

let count = 0;
function generateId(): string {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

interface ToastStore {
  state: ToastState;
  listeners: Array<(state: ToastState) => void>;
  getState: () => ToastState;
  setState: (nextState: ToastState | ((state: ToastState) => ToastState)) => void;
  subscribe: (listener: (state: ToastState) => void) => () => void;
}

const toastStore: ToastStore = {
  state: {
    toasts: [],
  },
  listeners: [],

  getState: () => toastStore.state,

  setState: (nextState) => {
    if (typeof nextState === 'function') {
      toastStore.state = nextState(toastStore.state);
    } else {
      toastStore.state = { ...toastStore.state, ...nextState };
    }

    toastStore.listeners.forEach((listener) => listener(toastStore.state));
  },

  subscribe: (listener) => {
    toastStore.listeners.push(listener);
    return () => {
      toastStore.listeners = toastStore.listeners.filter((l) => l !== listener);
    };
  },
};

export const toast = (props: Omit<ToastProps, 'id'>): ToastReturn => {
  const id = generateId();

  const update = (newProps: Partial<ToastProps>) =>
    toastStore.setState((state) => ({
      ...state,
      toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...newProps } : t)),
    }));

  const dismiss = () =>
    toastStore.setState((state) => ({
      ...state,
      toasts: state.toasts.filter((t) => t.id !== id),
    }));

  toastStore.setState((state) => ({
    ...state,
    toasts: [{ ...props, id, variant: props.variant || 'default' }, ...state.toasts].slice(
      0,
      TOAST_LIMIT
    ),
  }));

  return {
    id,
    dismiss,
    update,
  };
};

export function useToast() {
  const [state, setState] = useState<ToastState>(toastStore.getState());

  useEffect(() => {
    const unsubscribe = toastStore.subscribe((newState) => {
      setState(newState);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    state.toasts.forEach((toastItem) => {
      if (toastItem.duration === Infinity) {
        return;
      }

      const timeout = setTimeout(() => {
        toastStore.setState((state) => ({
          ...state,
          toasts: state.toasts.filter((t) => t.id !== toastItem.id),
        }));
      }, toastItem.duration || 5000);

      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [state.toasts]);

  return {
    toast,
    toasts: state.toasts,
  };
}
